/**
 * AUTH CONTEXT
 * Key fix: authReady flag — ProtectedRoute waits until session restore
 * attempt completes before deciding to redirect. Eliminates blank screen.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useEmail, EMAIL_EVENTS } from './EmailContext';
import { socialAuthApi } from '../services/socialAuthApi';
import authService from '../services/authService';
export const AuthContext = createContext(null);
const IS_MOCK = process.env.REACT_APP_MOCK !== 'false';

function normalizeUser(u) {
  if (!u) return null;
  const firstName = u.firstName || u.name?.split(' ')[0] || '';
  const lastName  = u.lastName  || u.name?.split(' ').slice(1).join(' ') || '';
  return {
    id:              u._id || u.id,
    role:            u.role || 'mentee',
    name:            u.name || `${firstName} ${lastName}`.trim() || 'User',
    firstName, lastName,
    email:           u.email || '',
    avatar:          u.avatar || '',
    avatarBg:        u.avatarBg || '1DB954',
    initials:        u.initials || `${(firstName[0]||'U')}${lastName[0]||''}`.toUpperCase(),
    headline:        u.headline || '',
    bio:             u.bio || '',
    location:        u.location || '',
    timezone:        u.timezone || 'IST (UTC+5:30)',
    isApproved:      u.isApproved ?? (u.role !== 'mentor'),
    isEmailVerified: u.isEmailVerified ?? false,
    profileComplete: u.profileComplete || 0,
    joinedDate:      u.joinedDate || (u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN',{month:'short',year:'numeric'}) : 'Recently'),
    termsAccepted:   u.termsAccepted || { accepted: true },
    mentorSlug:      u.mentorSlug || u.mentor?.slug || '',
    linkedinUrl:     u.linkedinUrl || '',
    githubUrl:       u.githubUrl   || '',
    phone:           u.phone       || '',
    goals:           u.goals       || [],
    skills:          u.skills      || [],
  };
}

const TC = { accepted:true, version:'2.1', privacyVersion:'1.4', method:'Email Registration' };
const MOCK_USERS = [
  { id:'u1', role:'mentor', name:'Amar Krishnan', firstName:'Amar', lastName:'Krishnan', email:'mentor@devbrains.com', password:'mentor123', avatarBg:'16a34a', initials:'AK', headline:'Senior Software Engineer @ Ex-Google', bio:'Ex-Google engineer with 8 years.', location:'Bengaluru', timezone:'IST (UTC+5:30)', isApproved:true, isEmailVerified:true, profileComplete:87, joinedDate:'Mar 2024', termsAccepted:{...TC}, mentorSlug:'amar-krishnan' },
  { id:'u2', role:'mentee', name:'Rahul Verma',   firstName:'Rahul', lastName:'Verma',   email:'mentee@devbrains.com', password:'mentee123', avatarBg:'2563eb', initials:'RV', headline:'Aspiring Full-Stack Developer', bio:'CS graduate.', location:'Mumbai', timezone:'IST (UTC+5:30)', isApproved:true, isEmailVerified:true, profileComplete:65, joinedDate:'Jan 2025', termsAccepted:{...TC} },
  { id:'u3', role:'admin',  name:'Admin DevBrains',firstName:'Admin',lastName:'DevBrains',email:'admin@devbrains.com',  password:'admin123',  avatarBg:'7c3aed', initials:'AD', headline:'Platform Administrator', bio:'', location:'Remote', timezone:'IST (UTC+5:30)', isApproved:true, isEmailVerified:true, profileComplete:100, joinedDate:'Jun 2023', termsAccepted:{...TC} },
];

export function AuthProvider({ children }) {
  // Restore from sessionStorage instantly — no flash
  const [user, setUser] = useState(() => {
    try { const s = sessionStorage.getItem('db_user'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [authReady,   setAuthReady]   = useState(() => {
    // If mock mode or no token stored → ready immediately
    if (IS_MOCK) return true;
    const token = localStorage.getItem('accessToken');
    return !token || token.startsWith('mock_') || !!sessionStorage.getItem('db_user');
  });
  const [authError,   setAuthError]   = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // ── Social Login (Google / GitHub / LinkedIn) ──────────────
  // Calls real backend API for social login
  const socialLogin = useCallback(async (provider, payload) => {
    setAuthLoading(true); setAuthError('');
    try {
      const data = await socialAuthApi({
        provider,
        credential: payload.credential,
        code: payload.code,
        email: payload.email,
        name: payload.name,
        role: payload.role,
      });
      if (!data.user) throw new Error('No user returned');
      setUser(data.user);
      try { sessionStorage.setItem('db_user', JSON.stringify(data.user)); } catch {}
      if (data.accessToken) sessionStorage.setItem('db_token', data.accessToken);
      if (data.refreshToken) sessionStorage.setItem('db_refresh', data.refreshToken);
      setAuthLoading(false);
      return data.user;
    } catch (err) {
      setAuthLoading(false);
      setAuthError(err.message || 'Social login failed');
      return null;
    }
  }, []);

  const emailCtx = useEmail();
  // useMemo keeps _sendEmail reference stable so useCallback deps don't change on every render
  const _sendEmail = useMemo(() => emailCtx?.sendEmail || (() => {}), [emailCtx?.sendEmail]);

  // Restore real session on mount (only if we have a token but no cached user)
  useEffect(() => {
    if (IS_MOCK || authReady) return;
    const token = localStorage.getItem('accessToken');
    if (!token || token.startsWith('mock_')) { setAuthReady(true); return; }

    authService.getMe()
      .then(res => {
        const u = normalizeUser(res.user || res);
        setUser(u);
        try { sessionStorage.setItem('db_user', JSON.stringify(u)); } catch {}
      })
      .catch(() => {
        // Token invalid / expired — clear it
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        try { sessionStorage.removeItem('db_user'); } catch {}
      })
      .finally(() => setAuthReady(true));
  }, []); // eslint-disable-line

  const _persist = useCallback((u) => {
    setUser(u);
    try { sessionStorage.setItem('db_user', JSON.stringify(u)); } catch {}
  }, []);

  // ── Login ──────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    setAuthLoading(true); setAuthError('');
    try {
      let safe;
      if (IS_MOCK) {
        await new Promise(r => setTimeout(r, 500));
        const found = MOCK_USERS.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
        if (!found) { setAuthError('Invalid credentials. Try mentor@devbrains.com / mentor123'); return null; }
        const { password: _, ...u } = found;
        safe = u;
      } else {
        const data = await authService.login(email, password);
        safe = normalizeUser(data.user);
        // Store tokens so apiClient interceptor can attach them to every request
        if (data.accessToken)  sessionStorage.setItem('db_token',   data.accessToken);
        if (data.refreshToken) sessionStorage.setItem('db_refresh',  data.refreshToken);
      }
      if (!safe.isEmailVerified) {
        setAuthError('verify:' + safe.email);
        return null;
      }
      // Apply photo uploaded during the registration flow (stored before email verification)
      try {
        const pendingPhoto = JSON.parse(sessionStorage.getItem('db_pending_photo') || 'null');
        if (pendingPhoto?.email?.toLowerCase() === safe.email?.toLowerCase() && pendingPhoto?.photoUrl) {
          safe = { ...safe, photoUrl: pendingPhoto.photoUrl };
          sessionStorage.removeItem('db_pending_photo');
        }
      } catch {}
      _persist(safe);
      _sendEmail(EMAIL_EVENTS.LOGIN_ALERT, {
        firstName: safe.firstName, email: safe.email,
        loginTime: new Date().toLocaleString('en-IN',{timeZone:'Asia/Kolkata'})+' IST',
        device: navigator.userAgent.includes('Chrome') ? 'Chrome Browser' : 'Web Browser',
        location: 'India', secureUrl: `${window.location.origin}/security`,
      });
      return safe;
    } catch (err) {
      console.error('[Login error]', err);
      // Backend returns EMAIL_NOT_VERIFIED — show the inline verify screen in the modal
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setAuthError('verify:' + email.toLowerCase());
      } else {
        setAuthError(err.message || 'Login failed. Please try again.');
      }
      return null;
    } finally { setAuthLoading(false); }
  }, [_persist, _sendEmail]);

  // ── Register ────────────────────────────────────────────────
  const register = useCallback(async (data) => {
    setAuthLoading(true); setAuthError('');
    try {
      let safe;
      if (IS_MOCK) {
        await new Promise(r => setTimeout(r, 500));
        safe = {
          id:'u'+Date.now(), role:'mentee',
          name:`${data.firstName} ${data.lastName}`, firstName:data.firstName, lastName:data.lastName,
          email:data.email, avatarBg:'1DB954',
          initials:`${data.firstName[0]}${data.lastName[0]}`.toUpperCase(),
          headline:'Aspiring Developer',
          bio:'', location:'', timezone:'IST', isApproved:true,
          isEmailVerified: false,
          profileComplete:20, joinedDate:'Today', termsAccepted:{...TC, acceptedAt:new Date().toISOString()},
        };
      } else {
        const res = await authService.register(data);
        // Always treat email registration as unverified — backend must not short-circuit this
        safe = normalizeUser({ ...res.user, isEmailVerified: false });
        // Backend generates a real token and returns the verifyUrl — use it if available
        if (res.verifyUrl) safe._verifyUrl = res.verifyUrl;
      }
      // Send verification email — always
      _sendEmail(EMAIL_EVENTS.EMAIL_VERIFICATION, {
        firstName: safe.firstName,
        email:     safe.email,
        verifyUrl: safe._verifyUrl || `${window.location.origin}/verify?token=${safe.id || 'mock'}`,
        platformUrl: 'www.devbrains.in',
      });
      // Don't log in until email is verified
      if (!safe.isEmailVerified) {
        return { ...safe, _pendingVerification: true };
      }
      _persist(safe);
      _sendEmail(EMAIL_EVENTS.REGISTER_WELCOME, {
        firstName:safe.firstName, email:safe.email,
        dashboardUrl:`${window.location.origin}/dashboard/${safe.role}`,
        platformUrl:'www.devbrains.in', unsubscribeUrl:`${window.location.origin}/unsubscribe`,
      });
      return safe;
    } catch (err) {
      setAuthError(err.message || 'Registration failed.');
      return null;
    } finally { setAuthLoading(false); }
  }, [_persist, _sendEmail]);

  // ── Social Login ────────────────────────────────────────────
  // const socialLogin = useCallback(async (provider, payload) => {
  //   setAuthLoading(true); setAuthError('');
  //   try {
  //     let safe;
  //     if (IS_MOCK) {
  //       await new Promise(r => setTimeout(r, 600));
  //       const names = { google:'Google', github:'GitHub', linkedin:'LinkedIn' };
  //       const role = payload.role || 'mentee';
  //       safe = {
  //         id:'social_'+provider+'_'+Date.now(), role,
  //         name:names[provider]+' User', firstName:names[provider], lastName:'User',
  //         email:`user@${provider}.com`, avatarBg:provider==='google'?'4285F4':provider==='github'?'24292e':'0A66C2',
  //         initials:provider[0].toUpperCase()+'U', headline:role==='mentor'?'Mentor':'Learner',
  //         bio:'', location:'', timezone:'IST', isApproved:role!=='mentor', profileComplete:30, joinedDate:'Today',
  //       };
  //     } else {
  //       const res = await authService.socialLogin({ provider, ...payload });
  //       safe = normalizeUser(res.user);
  //     }
  //     _persist(safe);
  //     return safe;
  //   } catch (err) {
  //     setAuthError(err.message || 'Social login failed.');
  //     return null;
  //   } finally { setAuthLoading(false); }
  // }, [_persist]);

  // ── Logout ──────────────────────────────────────────────────
  const logout = useCallback(async () => {
    if (!IS_MOCK) { try { await authService.logout(); } catch {} }
    setUser(null); setAuthError('');
    try { sessionStorage.removeItem('db_user'); } catch {}
  }, []);

  // ── Update user ─────────────────────────────────────────────
  const updateUser = useCallback(async (patch) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      try { sessionStorage.setItem('db_user', JSON.stringify(updated)); } catch {}
      return updated;
    });
    if (!IS_MOCK) {
      try {
        const { default: apiClient } = await import('../services/apiClient');
        await apiClient.put('/users/me', patch);
      } catch {}
    }
  }, []);

  const clearError = useCallback(() => setAuthError(''), []);

  // ── Forgot password ─────────────────────────────────────────
  const forgotPassword = useCallback(async (email) => {
    setAuthLoading(true); setAuthError('');
    try {
      await authService.forgotPassword(email, window.location.origin);
      _sendEmail(EMAIL_EVENTS.PASSWORD_RESET, {
        firstName: email.split('@')[0],
        email,
        resetUrl: `${window.location.origin}/reset-password?token=${IS_MOCK ? 'mock_reset_' + Date.now() : 'pending'}`,
        platformUrl: 'www.devbrains.in',
      });
      return true;
    } catch (err) {
      setAuthError(err.message || 'Failed to send reset email. Please try again.');
      return false;
    } finally { setAuthLoading(false); }
  }, [_sendEmail]);

  // ── Reset password ──────────────────────────────────────────
  const resetPassword = useCallback(async (token, newPassword) => {
    setAuthLoading(true); setAuthError('');
    try {
      await authService.resetPassword(token, newPassword);
      return true;
    } catch (err) {
      setAuthError(err.message || 'Reset failed. The link may have expired.');
      return false;
    } finally { setAuthLoading(false); }
  }, []);

  // ── Verify email (called from /verify?token=... link) ──────
  const verifyEmail = useCallback(async (token) => {
    try {
      if (!IS_MOCK) await authService.verifyEmail(token);
    } catch {}
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, isEmailVerified: true };
      try { sessionStorage.setItem('db_user', JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const resendVerification = useCallback(async (email) => {
    try {
      await authService.resendVerification(email);
      _sendEmail(EMAIL_EVENTS.EMAIL_VERIFICATION, {
        firstName: email.split('@')[0],
        email,
        verifyUrl: `${window.location.origin}/verify?token=resend_${Date.now()}`,
        platformUrl: 'www.devbrains.in',
      });
      return true;
    } catch (err) {
      setAuthError(err.message || 'Failed to resend verification email.');
      return false;
    }
  }, [_sendEmail]);

  return (
    <AuthContext.Provider value={{ user, authReady, login, logout, register, socialLogin, updateUser, verifyEmail, authError, authLoading, clearError, resendVerification, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
export const DASHBOARD_ROUTES = { mentor:'/dashboard/mentor', mentee:'/dashboard/mentee', admin:'/dashboard/admin' };
